import { useRootStore } from '@/store/root';

const now = new Date();

const getWeek = (dateToCheck: Date = new Date()): number => {
  const date = new Date(dateToCheck);
  const utcDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );

  // Set date to Thursday of the current week
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - (utcDate.getUTCDay() || 7));

  // Get the first Thursday of the year
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 4));

  // Adjust to the previous Monday to align the year start week
  yearStart.setUTCDate(
    yearStart.getUTCDate() - (yearStart.getUTCDay() || 7) + 1,
  );

  // Calculate the week number
  return Math.ceil(
    ((utcDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  );
};

export const isDate = (value: string | number | Date): value is string => {
  const dateAsString = new Date(value).toString();
  return (
    !!value &&
    value !== 'null' &&
    dateAsString !== 'Invalid Date' &&
    !Number.isNaN(new Date(value))
  );
};

export const getRawDate = (value: string | number | Date) => {
  if (isDate(value)) {
    return new Date(value).toLocaleDateString('fr-CA');
  }
  return null;
};

export const getRawTime = (value: string | number | Date) => {
  if (isDate(value)) {
    return new Date(value).toLocaleTimeString('it-IT');
  }
  return null;
};

const getDate = function (value: Date | string): {
  date: string;
  month: string;
  year: string;
} {
  if (isDate(value)) {
    const dateValue = new Date(value);
    const [date = '', month = '', year = ''] = [
      dateValue.getDate(),
      dateValue.getMonth() + 1,
      dateValue.getFullYear(),
    ].map((n) => (n < 10 ? `0${n}` : `${n}`));
    return { date, month, year };
  }
  return { date: '', month: '', year: '' };
};

export const timeFromFormat = (
  value: Date | string,
  is24HrFormat = false,
): string => {
  if (!value) {
    return value;
  }

  if (isDate(value)) {
    const timeValue = new Date(value);
    value = `${timeValue.getHours()}:${timeValue.getMinutes()}:${timeValue.getSeconds()}`;
  }
  const userGroups = useRootStore().getUserGroups;
  let [hours = '0', mins = '0'] = String(value).split(':');
  mins = mins.length < 2 ? `0${mins}` : mins;
  if (!is24HrFormat && userGroups.timeFormat === 'hh:mm:ss') {
    const time = Number(hours) < 12 ? 'AM' : 'PM';
    hours = String(Number(hours) % 12 || 12);
    hours = hours.length < 2 ? `0${hours}` : hours;

    return `${hours}:${mins} ${time}`;
  }
  hours = hours.length < 2 ? `0${hours}` : hours;
  return `${hours}:${mins}`.trim();
};

export const dateFromFormat = (value: Date | string) => {
  if (isDate(value)) {
    const { dateFormat = '' } = useRootStore().getUserGroups;
    const { date = '', month = '', year = '' } = getDate(value);
    return dateFormat
      ?.replace('mm', month)
      .replace('dd', date)
      .replace('yyyy', year);
  }
  return value;
};

export const dateTimeFromFormat = (
  inputDate: Date | string | number,
): string => {
  if (isDate(inputDate)) {
    const date = new Date(inputDate);
    return `${dateFromFormat(date) as string} ${timeFromFormat(date)}`;
  }
  const date = String(inputDate).slice(0, 10);
  const time = String(inputDate).slice(11, 19);
  return `${dateFromFormat(date) as string} ${timeFromFormat(time)}`;
};

export const shortDate = (value: Date) => {
  if (!value) {
    return null;
  }
  const { dateFormat = '' } = useRootStore().getUserGroups;
  const { date = '', month = '', year = '' } = getDate(value);
  const formattedDate = dateFormat
    .replace('mm', month)
    .replace('dd', date)
    .replace('yyyy', year);
  return dateFormat === 'dd.mm.yyyy'
    ? formattedDate.replace(year, '')
    : formattedDate;
};

export const shortYear = () => {
  const { dateFormat = '' } = useRootStore().getUserGroups;

  if (dateFormat.search('yyyy') !== -1) {
    return dateFormat.replace('yyyy', 'yy');
  }
  if (dateFormat.search('yy') !== -1) {
    return dateFormat.replace('yy', 'y');
  }

  return dateFormat;
};

export const isOlderThanLastMonth = (
  dateToCheck: Date | string | number = new Date(),
): boolean => {
  const date = new Date(dateToCheck);
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  lastMonth.setDate(1);

  return date.getTime() < lastMonth.getTime();
};

export const isOlderThan24h = (
  dateToCheck: Date | string | number = new Date(),
): boolean => {
  const date = new Date(dateToCheck);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return date <= yesterday;
};

export const isToday = (
  dateToCheck: Date | string | number = new Date(),
): boolean => {
  const date = new Date(dateToCheck);
  return (
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  );
};

export const isThisWeek = (
  dateToCheck: Date | string | number = new Date(),
): boolean => {
  const date = new Date(dateToCheck);
  return (
    getWeek(date) === getWeek(now) && date.getFullYear() === now.getFullYear()
  );
};

export const isLastWeek = (
  dateToCheck: Date | string | number = new Date(),
): boolean => {
  const date = new Date(dateToCheck);

  const currentWeek = getWeek(now);
  const currentYear = now.getFullYear();

  const weekToCheck = getWeek(date);
  const yearToCheck = date.getFullYear();

  return (
    (yearToCheck === currentYear && weekToCheck === currentWeek - 1) ||
    // Check edge case where the last week of the year is in first week of the current year
    (yearToCheck === currentYear - 1 &&
      weekToCheck === getWeek(new Date(currentYear - 1, 11, 31)))
  );
};

export const isLastMonth = (
  dateToCheck: Date | string | number = new Date(),
): boolean => {
  const date = new Date(dateToCheck);
  const expectedMonth = (date.getMonth() + 1) % 12;
  const yearOffset =
    expectedMonth === 0 ? date.getFullYear() + 1 : date.getFullYear();
  return expectedMonth === now.getMonth() && yearOffset === now.getFullYear();
};

export const isInFuture = (
  dateToCheck: Date | string | number = new Date(),
): boolean => new Date(dateToCheck).getTime() > now.getTime();

export const isInPast = (
  dateToCheck: Date | string | number = new Date(),
): boolean => new Date(dateToCheck).getTime() < now.getTime();

export const formatDate = (
  inputDate: Date | string | number,
  delimiter = '-',
): string => {
  const date = new Date(inputDate);
  return `${date.getFullYear()}${delimiter}${(date.getMonth() + 1)
    .toString()
    .padStart(2, '0')}${delimiter}${date
    .getDate()
    .toString()
    .padStart(2, '0')}`;
};

export const addDaysToDate = (
  inputDate: Date | string | number,
  daysToAdd: number,
): Date => {
  const date = new Date(inputDate);
  date.setDate(date.getDate() + daysToAdd);
  return date;
};

export const formatTime = (inputDate: Date | string | number): string =>
  new Date(inputDate).toLocaleTimeString();
